/** My devices (STORY-075): what has run lessons for this teacher, with revoke-and-wipe. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { TeacherChrome } from "../teacher/Chrome";
import { listDevices, revokeDevice } from "../teacher/api";
import { Button, useConfirm } from "../ui";

export function Devices() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const devices = useQuery({ queryKey: ["devices"], queryFn: listDevices });
  const revoke = useMutation({ mutationFn: (id: string) => revokeDevice(id), onSuccess: () => void qc.invalidateQueries({ queryKey: ["devices"] }) });
  const fmt = (iso: string) => new Date(iso).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const card = "rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 shadow-sm";
  return (
    <TeacherChrome title={t("devices.title")}>
      <header className="-mt-2 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
        <Link to="/t" className="underline">{t("devices.toCourses")}</Link>
        <p className="measure text-[color:var(--ink-2)]">{t("devices.lede")}</p>
      </header>
      {devices.data && devices.data.length === 0 && <p className={card}>{t("devices.empty")}</p>}
      <ul className="flex flex-col gap-2">
        {devices.data?.map((d) => (
          <li key={d.device_id} className={`${card} flex flex-wrap items-center gap-3 ${d.revoked_at ? "opacity-60" : ""}`}>
            <div className="min-w-0 flex-1">
              <p className="font-medium">{d.label || d.device_id} <span className="text-xs text-[color:var(--ink-2)]">{d.platform}</span></p>
              <p className="text-xs text-[color:var(--ink-2)]">
                {t("devices.registered", { at: fmt(d.registered_at) })} · {t("devices.seen", { at: fmt(d.last_seen_at) })}
                {d.revoked_at && ` · ${t("devices.revoked", { at: fmt(d.revoked_at) })}`}
              </p>
              <p className="font-mono text-xs text-[color:var(--ink-2)]">{d.device_id}</p>
            </div>
            {!d.revoked_at && (
              <Button
                variant="destructive"
                loading={revoke.isPending && revoke.variables === d.device_id}
                onClick={async () => {
                  if (await confirm({ title: t("devices.revokeTitle", { label: d.label || d.device_id }), body: t("devices.confirm"), confirmLabel: t("devices.revoke"), destructive: true })) revoke.mutate(d.device_id);
                }}
              >
                {t("devices.revoke")}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </TeacherChrome>
  );
}
